import type { Lesson } from '@yuan-math/curriculum';
import { figure, insight, lesson, list, math, practice, steps, table, text, tip, warning } from './authoring.ts';

/**
 * Grade 6 number: where arithmetic widens.
 *
 * Dividing by a fraction, decimal multiplication and division, negative numbers
 * and distance from zero, exponents, prime factorisation and common factors.
 * Most of these break a rule the student has spent years relying on — dividing
 * makes things smaller, multiplying makes them bigger, you cannot take seven
 * from three — so each lesson names the broken rule explicitly rather than
 * quietly moving past it.
 */
export const NUMBER_MIDDLE_LESSONS: Lesson[] = [
  lesson({
    id: 'divide-fractions-01',
    unitId: 'unit-fraction-division',
    conceptId: 'divide-fractions',
    title: 'Dividing Fractions',
    summary: 'Dividing by a fraction asks how many of it fit inside — which is why the answer can be bigger than what you started with.',
    objectives: [
      'Interpret division by a fraction as "how many fit"',
      'Divide by multiplying by the reciprocal',
      'Explain why the answer can be larger than the dividend',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'Another rule that stops being true',
        blocks: [
          text('You have spent years knowing that dividing makes numbers smaller. Then this happens:'),
          math('6 \\div \\frac{1}{2} = 12'),
          text('Twelve is bigger than six. Nothing has gone wrong — but the sentence "dividing makes things smaller" was never quite true, and here is where it fails.'),
        ],
      },
      intuition: {
        title: '"How many fit inside?"',
        blocks: [
          text('Read 6 ÷ 1/2 out loud as a question about fitting: **how many halves fit into 6?**'),
          text('Each whole contains 2 halves. Six wholes therefore contain 12 halves. The answer is 12, and you did not need a rule to get it — just the question.'),
          insight('Division has always been this question. 12 ÷ 3 asks how many 3s fit into 12. Fractions do not change the question, only the size of the thing being fitted.'),
          warning('If the thing you are fitting is smaller than 1, more of them fit, so the answer grows. That is the whole mystery.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'fraction-bars',
              bars: [
                { parts: 2, shaded: 2, label: '1 whole' },
                { parts: 2, shaded: 2, label: '1 whole' },
                { parts: 2, shaded: 2, label: '1 whole' },
              ],
            },
            'Three wholes, each cut into halves: six halves fit. Six wholes would hold twelve.',
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Compute 3/4 ÷ 2/3.'),
          text('Asking "how many two-thirds fit into three-quarters?" is harder to picture, so here is the rule that always gives the same answer:'),
          steps([
            { label: 'Keep the first fraction exactly as it is.', latex: '\\frac{3}{4}' },
            { label: 'Turn the second one upside down. This is its reciprocal.', latex: '\\frac{2}{3} \\rightarrow \\frac{3}{2}' },
            { label: 'Change the division to a multiplication.', latex: '\\frac{3}{4} \\times \\frac{3}{2}' },
            { label: 'Multiply across.', latex: '\\frac{9}{8}' },
            { label: 'Sense-check: 2/3 is a bit smaller than 3/4, so slightly more than one fits.', latex: '\\frac{9}{8} = 1\\tfrac{1}{8}' },
          ]),
          warning('It is the fraction you are dividing **by** that flips — the second one. Flipping the first is the single commonest mistake in this topic.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('divide-fractions', 4, 'Divide. Simplify your answer.')] },
      independent: { title: 'On your own', blocks: [practice('divide-fractions', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('divide-fractions', 3, 'Word problems, where you have to decide that division is what is needed.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Division asks how many of one thing fit into another.',
            'Keep the first fraction, flip the second, multiply.',
            'Only the divisor — the second fraction — is inverted.',
            'Dividing by something less than 1 makes the answer bigger.',
          ]),
          tip('Always sense-check. If you divide by something smaller than 1 and your answer shrank, you flipped the wrong fraction.'),
        ],
      },
    },
  }),

  lesson({
    id: 'integers-01',
    unitId: 'unit-integers',
    conceptId: 'integers',
    title: 'Negative Numbers',
    summary: 'The number line does not stop at zero. Numbers below it obey rules you can see rather than memorise.',
    objectives: [
      'Place and compare negative numbers on the number line',
      'Add and subtract with negative numbers',
      'Explain why subtracting a negative adds',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'Below zero',
        blocks: [
          text('What is 3 − 5?'),
          text('For years the honest answer was "you cannot do that". You can. It is −2, and it means something real: two below zero. A temperature, a debt, a floor beneath the ground floor, a metre under sea level.'),
          math('3 - 5 = -2'),
        ],
      },
      intuition: {
        title: 'The line keeps going',
        blocks: [
          text('The number line you have used since Grade 1 simply continues to the left of zero. Everything you know still holds: numbers get smaller as you move left.'),
          warning('Which means −8 is **less** than −3. Not more. The 8 is bigger than the 3, but −8 sits further left, and further left is always smaller. Eight degrees below zero is colder than three degrees below zero.', 'The trap'),
          insight('Two numbers the same distance from zero on opposite sides — like 5 and −5 — are called opposites. Adding a number to its opposite always gives zero, which is what makes subtraction and negatives fit together.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'number-line', from: -6, to: 6, step: 1, marks: [{ at: -3, label: '−3' }, { at: 4, label: '4' }] },
            'Left is smaller, always. −3 sits to the left of 4, and also to the left of 0.',
          ),
          text('Adding moves you right. Subtracting moves you left. That is true everywhere on the line, positive or negative.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Compute −4 + 7, and then 5 − (−3).'),
          steps([
            { label: 'Start at −4 and move 7 to the right.', latex: '-4 + 7' },
            { label: 'You pass zero after 4 steps, with 3 left to take.', latex: '3' },
          ]),
          text('Now the one that looks strange:'),
          steps([
            { label: 'Subtracting means removing. Here you are removing a negative.', latex: '5 - (-3)' },
            { label: 'Removing a debt of 3 leaves you 3 better off, so this moves right, not left.', latex: '5 + 3' },
            { label: 'Answer.', latex: '8' },
          ]),
          insight('Subtracting a negative adds. Not because of a rule about two minus signs, but because taking away something that was pulling you down lets you rise.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('integers', 4)] },
      independent: { title: 'On your own', blocks: [practice('integers', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('integers', 3, 'Ordering, and problems set in real contexts.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'The number line continues below zero.',
            'Further left is always smaller: −8 < −3.',
            'Adding moves right; subtracting moves left.',
            'Subtracting a negative adds.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'exponents-01',
    unitId: 'unit-exponents',
    conceptId: 'exponents',
    title: 'Exponents',
    summary: 'A compact way to write repeated multiplication — and a new first step in the order of operations.',
    objectives: [
      'Read and evaluate expressions with exponents',
      'Distinguish repeated multiplication from ordinary multiplication',
      'Place exponents correctly in the order of operations',
    ],
    estimatedMinutes: 25,
    sections: {
      introduction: {
        title: 'Shorthand for repetition',
        blocks: [
          text('Multiplication is shorthand for repeated addition. Exponents are shorthand for repeated multiplication.'),
          math('2 \\times 2 \\times 2 \\times 2 \\times 2 = 2^5 = 32'),
          text('The big number is the **base** — what is being multiplied. The small raised number is the **exponent** — how many times.'),
        ],
      },
      intuition: {
        title: 'The exponent counts, it does not multiply',
        blocks: [
          warning('2³ is not 2 × 3. It is 2 × 2 × 2 = 8. The exponent says *how many copies of the base to multiply together*, not what to multiply the base by.', 'Read this twice'),
          table(['Expression', 'Means', 'Value'], [
            ['2³', '2 × 2 × 2', '8'],
            ['3²', '3 × 3', '9'],
            ['5¹', '5', '5'],
            ['10⁴', '10 × 10 × 10 × 10', '10,000'],
          ]),
          insight('Powers of ten are why place value works. 10³ = 1000 is exactly the thousands place, which is why our number system and exponents are really the same idea twice.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          text('Squaring makes a square, and cubing makes a cube. The names are literal:'),
          figure({ kind: 'array', rows: 4, columns: 4, label: '4² = 16' }, 'Four rows of four.'),
          figure({ kind: 'prism', length: 3, width: 3, height: 3, labels: { length: '3', width: '3', height: '3' } }, '3³ = 27 unit cubes fill this.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Evaluate 4 + 3 × 2³.'),
          steps([
            { label: 'Exponents come before multiplication.', latex: '2^3 = 2 \\times 2 \\times 2 = 8' },
            { label: 'Now multiply.', latex: '3 \\times 8 = 24' },
            { label: 'Then add.', latex: '4 + 24 = 28' },
          ]),
          tip('Exponents slot into the order of operations directly after brackets, and before multiplication and division. Brackets, exponents, multiply/divide, add/subtract.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('exponents', 4)] },
      independent: { title: 'On your own', blocks: [practice('exponents', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('exponents', 3)] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'The base is multiplied by itself; the exponent counts how many times.',
            '2³ = 8, not 6.',
            'Any number to the power 1 is itself.',
            'Exponents come straight after brackets in the order of operations.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'prime-factorization-01',
    unitId: 'unit-exponents',
    conceptId: 'prime-factorization',
    title: 'Prime Factorisation',
    summary: 'Every whole number breaks into primes, and there is only one way to do it.',
    objectives: [
      'Identify prime and composite numbers',
      'Write a number as a product of primes',
      'Use prime factorisation to find a GCF or LCM',
    ],
    estimatedMinutes: 25,
    sections: {
      introduction: {
        title: 'The atoms of arithmetic',
        blocks: [
          text('Some numbers can be broken into smaller factors: 12 is 3 × 4. Some cannot: 7 is only 1 × 7.'),
          text('Numbers that cannot be broken down are called **prime**. They are the atoms — everything else is built from them, and the building can be done in exactly one way.'),
          math('60 = 2 \\times 2 \\times 3 \\times 5 = 2^2 \\times 3 \\times 5'),
        ],
      },
      intuition: {
        title: 'Keep splitting until you cannot',
        blocks: [
          text('Take any number and split it into a pair of factors. Split those. Keep going. You always stop at primes, and — remarkably — you always stop at the *same* primes, whichever splits you chose along the way.'),
          insight('This is important enough to have a grand name: the Fundamental Theorem of Arithmetic. Every whole number above 1 has exactly one prime factorisation.'),
          warning('1 is not prime. If it were, factorisations would not be unique — you could paste in as many 1s as you liked and never be finished.'),
        ],
      },
      visual: {
        title: 'A factor tree',
        blocks: [
          math('60 \\rightarrow 6 \\times 10 \\rightarrow (2 \\times 3) \\times (2 \\times 5)'),
          text('Starting differently changes the route but never the destination:'),
          math('60 \\rightarrow 4 \\times 15 \\rightarrow (2 \\times 2) \\times (3 \\times 5)'),
          table(['Number', 'Prime factorisation'], [
            ['12', '2² × 3'],
            ['30', '2 × 3 × 5'],
            ['64', '2⁶'],
            ['97', '97 (it is prime)'],
          ]),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Write 84 as a product of primes.'),
          steps([
            { label: 'It is even, so divide by 2.', latex: '84 = 2 \\times 42' },
            { label: 'Still even.', latex: '42 = 2 \\times 21' },
            { label: '21 is not even; try 3.', latex: '21 = 3 \\times 7' },
            { label: '7 is prime, so stop.', latex: '84 = 2 \\times 2 \\times 3 \\times 7' },
            { label: 'Write repeats using exponents.', latex: '84 = 2^2 \\times 3 \\times 7' },
          ]),
          tip('Work through the primes in order — 2, 3, 5, 7, 11 — and you will never miss one.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('prime-factorization', 4)] },
      independent: { title: 'On your own', blocks: [practice('prime-factorization', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('prime-factorization', 3)] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A prime has exactly two factors: 1 and itself.',
            '1 is not prime.',
            'Every whole number above 1 factorises into primes in exactly one way.',
            'Repeated factors are written with exponents.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'decimal-operations-01',
    unitId: 'unit-decimal-operations',
    conceptId: 'decimal-operations',
    title: 'Multiplying and Dividing Decimals',
    summary: 'The digits are the ones you already know how to get. The only new question is where the point goes.',
    objectives: [
      'Multiply decimals by counting decimal places',
      'Divide by a decimal by shifting both numbers',
      'Estimate first, so a misplaced point is caught',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'One new question, and only one',
        blocks: [
          text('You already know that 24 × 3 = 72. So you already know almost everything about this:'),
          math('2.4 \\times 0.3 = 0.72'),
          text('The digits 7 and 2 came from the multiplication you have done since Grade 3. Everything new in this lesson is the question of where to put the point.'),
        ],
      },
      intuition: {
        title: 'Why the point moves left',
        blocks: [
          text('Read 0.3 as "three tenths". Multiplying by a tenth makes things ten times smaller, so multiplying by three tenths lands somewhere below the number you started with, not above it.'),
          insight('2.4 × 0.3 has to be smaller than 2.4, because you are taking less than one of it. Seventy-two hundredths — 0.72 — is the only sensible place for those digits.'),
          text('The bookkeeping version of that argument is counting. 2.4 has one digit after the point and 0.3 has one, so the answer has two:'),
          math('2.4 \\times 0.3 = 0.72 \\qquad (1 + 1 = 2 \\text{ decimal places})'),
          warning('Division is the opposite instinct. Dividing **by** a number smaller than 1 makes the answer bigger: 6 ÷ 0.5 = 12, because twelve halves fit into six. It is the same idea as dividing by a fraction.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'grid', width: 10, height: 10, shaded: [[0, 0], [1, 0], [2, 0], [0, 1], [1, 1], [2, 1]] },
            'The big square is 1, so each small square is 0.01. Six of them shaded is 0.06 — which is 0.2 × 0.3, two tenths of three tenths.',
          ),
          text('Tenths of tenths are hundredths. That is the whole of the place-counting rule, drawn.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Compute 1.25 × 0.4, and then 7.2 ÷ 0.8.'),
          steps([
            { label: 'Ignore both points and multiply whole numbers.', latex: '125 \\times 4 = 500' },
            { label: 'Count the decimal places in the question: two, then one.', latex: '2 + 1 = 3' },
            { label: 'Put three decimal places into the answer.', latex: '0.500 = 0.5' },
            { label: 'Check: 0.4 is under a half, so the answer should be under half of 1.25.', latex: '0.5 < 0.625' },
          ]),
          text('Now the division. A decimal divisor is awkward, so move it out of the way:'),
          steps([
            { label: 'Multiply both numbers by 10, which leaves the answer unchanged.', latex: '7.2 \\div 0.8 = 72 \\div 8' },
            { label: 'Divide.', latex: '72 \\div 8 = 9' },
            { label: 'Check: 0.8 is less than 1, so the answer must be bigger than 7.2. It is.', latex: '9 > 7.2' },
          ]),
          tip('Both numbers must be shifted by the same amount. 7.2 ÷ 0.8 becomes 72 ÷ 8, never 72 ÷ 0.8.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('decimal-operations', 4)] },
      independent: { title: 'On your own', blocks: [practice('decimal-operations', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('decimal-operations', 3, 'Money and measurement, where a misplaced point is a real mistake.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Multiply as if the points were not there, then count decimal places.',
            'The answer has as many decimal places as the two factors together.',
            'To divide by a decimal, shift both numbers until the divisor is whole.',
            'Multiplying by less than 1 shrinks; dividing by less than 1 grows.',
          ]),
          tip('Estimate before you calculate. 4.9 × 3.1 is about 15, so an answer of 1.519 or 151.9 is a misplaced point rather than bad arithmetic.'),
        ],
      },
    },
  }),

  lesson({
    id: 'gcf-lcm-01',
    unitId: 'unit-exponents',
    conceptId: 'gcf-lcm',
    title: 'Greatest Common Factor and Lowest Common Multiple',
    summary: 'Two opposite questions about what a pair of numbers share, answered by the same prime factorisation.',
    objectives: [
      'Find the greatest common factor of two numbers',
      'Find the lowest common multiple of two numbers',
      'Decide which of the two a problem is asking for',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'What do 12 and 18 share?',
        blocks: [
          text('That question has two honest answers, and they point in opposite directions.'),
          text('**Downwards:** the biggest number that divides into both. For 12 and 18 that is 6 — the *greatest common factor*.'),
          text('**Upwards:** the smallest number that both divide into. For 12 and 18 that is 36 — the *lowest common multiple*.'),
          math('\\gcd(12, 18) = 6 \\qquad \\operatorname{lcm}(12, 18) = 36'),
        ],
      },
      intuition: {
        title: 'One goes down, one goes up',
        blocks: [
          text('A factor of a number is never bigger than it. A multiple of a number is never smaller than it. That single fact tells you which of the two answers you are looking at.'),
          warning('The GCF of 12 and 18 cannot be larger than 12, and their LCM cannot be smaller than 18. If your GCF comes out bigger than the smaller number, or your LCM smaller than the larger one, you have swapped them.', 'The check that catches the swap'),
          table(['Question in words', 'What it wants'], [
            ['The largest identical groups you can split both into', 'GCF'],
            ['Cutting two ribbons into equal pieces, as long as possible', 'GCF'],
            ['Two buses leaving together — when do they next coincide?', 'LCM'],
            ['The smallest denominator two fractions can share', 'LCM'],
          ]),
        ],
      },
      visual: {
        title: 'Seeing it in the primes',
        blocks: [
          text('Break both numbers into primes and the two answers are read straight off:'),
          math('12 = 2^2 \\times 3 \\qquad 18 = 2 \\times 3^2'),
          table(['', 'Twos', 'Threes', 'Result'], [
            ['GCF — the lower power of each', '2¹', '3¹', '2 × 3 = 6'],
            ['LCM — the higher power of each', '2²', '3²', '4 × 9 = 36'],
          ]),
          insight('Lower for the factor, higher for the multiple. The GCF has to divide both, so it can only claim what both have; the LCM has to be divisible by both, so it must carry everything either one needs.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A florist has 24 roses and 36 tulips, and wants identical bunches with none left over. What is the largest number of bunches, and what is in each?'),
          steps([
            { label: 'Identical bunches with nothing left over means a common factor; largest means greatest.', latex: '\\gcd(24, 36)' },
            { label: 'Factorise both.', latex: '24 = 2^3 \\times 3, \\quad 36 = 2^2 \\times 3^2' },
            { label: 'Take the lower power of each shared prime.', latex: '2^2 \\times 3 = 12' },
            { label: 'So twelve bunches.', latex: '24 \\div 12 = 2 \\text{ roses}, \\quad 36 \\div 12 = 3 \\text{ tulips}' },
          ]),
          tip('For small numbers, listing works and is worth doing once: the factors of 24 are 1, 2, 3, 4, 6, 8, 12, 24 and the factors of 36 are 1, 2, 3, 4, 6, 9, 12, 18, 36. The largest in both lists is 12, which is what the primes said.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('gcf-lcm', 4)] },
      independent: { title: 'On your own', blocks: [practice('gcf-lcm', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('gcf-lcm', 3, 'Word problems, where deciding which one is being asked for is the work.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'The GCF divides both numbers, so it is never bigger than the smaller one.',
            'The LCM is divisible by both, so it is never smaller than the larger one.',
            'From the prime factorisations: lower powers give the GCF, higher powers the LCM.',
            'Splitting into equal groups is a GCF question; things coinciding again is an LCM question.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'absolute-value-opposites-01',
    unitId: 'unit-integers',
    conceptId: 'absolute-value-opposites',
    title: 'Opposites and Absolute Value',
    summary: 'How far a number is from zero, ignoring which side it is on — and why that answer is never negative.',
    objectives: [
      'Find the opposite of a number',
      'Evaluate absolute value expressions',
      'Distinguish "which is larger" from "which is further from zero"',
    ],
    estimatedMinutes: 25,
    sections: {
      introduction: {
        title: 'Two questions that sound like one',
        blocks: [
          text('A diver is 30 metres below the surface. A bird is 30 metres above it.'),
          text('Which is higher? The bird, comfortably: −30 is less than 30. Which is further from the surface? Neither — they are both 30 metres away.'),
          text('Those are two different questions, and mathematics has two different tools for them. Position is the number itself. Distance from zero is its **absolute value**.'),
          math('|-30| = 30 \\qquad |30| = 30'),
        ],
      },
      intuition: {
        title: 'Distance has no sign',
        blocks: [
          text('The **opposite** of a number is its mirror image across zero: the opposite of 7 is −7, and the opposite of −7 is 7. Opposites are the same distance from zero in different directions.'),
          text('The **absolute value** throws the direction away and keeps the distance. Written with two straight bars, it answers "how far?" — and a distance is never negative.'),
          warning('|−8| is 8, not −8. The bars do not make a number negative and they do not make it positive; they measure how far it is from zero, and nothing is ever a negative distance from anywhere.', 'The one to be careful with'),
          insight('The opposite of a negative number is positive, which is why −(−5) = 5. That is the same fact as "subtracting a negative adds", seen from a different angle.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'number-line', from: -8, to: 8, step: 2, marks: [{ at: -5, label: '−5' }, { at: 5, label: '5' }] },
            'Opposites sit at equal distances on opposite sides. Both of these are five steps from zero, so both have absolute value 5.',
          ),
          text('Reading the line left to right gives order. Reading it outwards from zero gives absolute value. −7 is *less than* −2, and it is *further from zero* than −2 — both at once, with no contradiction.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Evaluate |−6| + |4|. Then decide which is greater, −9 or −4, and which has the greater absolute value.'),
          steps([
            { label: 'Absolute value first: −6 is six from zero.', latex: '|-6| = 6' },
            { label: 'And 4 is four from zero.', latex: '|4| = 4' },
            { label: 'Now add.', latex: '6 + 4 = 10' },
          ]),
          text('And the comparison:'),
          steps([
            { label: '−4 sits to the right of −9 on the line, so it is greater.', latex: '-4 > -9' },
            { label: 'But −9 is nine from zero and −4 is only four.', latex: '|-9| = 9 > 4 = |-4|' },
            { label: 'The smaller number has the larger absolute value. Both statements are true.', latex: '-9 < -4 \\text{ and } |-9| > |-4|' },
          ]),
          tip('A debt of £9 is worse than a debt of £4 — a smaller balance, a bigger debt. That sentence is the whole lesson.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('absolute-value-opposites', 4)] },
      independent: { title: 'On your own', blocks: [practice('absolute-value-opposites', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('absolute-value-opposites', 3)] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'The opposite of a number is its mirror image across zero.',
            'Absolute value is distance from zero, so it is never negative.',
            '|−8| = 8 and |8| = 8.',
            'On the negative side, "further from zero" and "greater" are different questions.',
          ]),
        ],
      },
    },
  }),
];
